import { useEffect, useSyncExternalStore } from "react";
import { writeLocal } from "./storage";

/**
 * UX finding 7 (2026-09-17 review): outside a `/p/:projectKey/*` route the sidebar's Plan/Operate/Register
 * groups had nothing to key their links off, so they rendered as empty headers with no way back to the board
 * a user came from — e.g. an item detail page at `/items/BMS-3` knows it belongs to project BMS, but AppShell
 * (the layout wrapping every route) had no way to learn that.
 *
 * A page that knows its own project reports it here; AppShell reads it to keep the nav populated. A plain
 * module-level store rather than React context, so a deeply-nested page can report before AppShell's own
 * render settles, and so unrelated pages (Home, Search, DevOps queue — portfolio-wide, not one project's)
 * don't need to thread a value they don't have.
 */
let hint: string | undefined;
const listeners = new Set<() => void>();

export function setActiveProjectHint(projectKey: string | undefined): void {
  if (hint === projectKey) return;
  hint = projectKey;
  // Also remembered as the last active project (AppShell's existing fallback for a route with no project of its
  // own at all, e.g. Home after this page is left) — the same key AppShell already writes for `/p/:projectKey/*`.
  if (projectKey) writeLocal("lastProject", projectKey);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string | undefined {
  return hint;
}

/** AppShell's side: the most recently reported project, or undefined if nothing has reported one. */
export function useActiveProjectHint(): string | undefined {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** A page's side: report the project it belongs to while mounted; clears its own hint on unmount, not another's. */
export function useReportActiveProject(projectKey: string | undefined): void {
  useEffect(() => {
    if (!projectKey) return;
    setActiveProjectHint(projectKey);
    return () => {
      setActiveProjectHint(undefined);
    };
  }, [projectKey]);
}
